import { GridCountryPlot } from "@/grid/GridCountryPlot";
import { GridEmptyOptions } from "@/grid/GridEmptyOptions";
import { GridMap } from "@/grid/GridMap";
import { GridPlot } from "@/grid/GridPlot";
import { GridSortableElement } from "@/grid/GridSortableElement";
import type { useCountryPlots } from "@/reducer/CountryPlotState";
import type { useCrossSections } from "@/reducer/CrossSectionState";
import type { useEnergyPlots } from "@/reducer/EnergyPlotsState";
import type { useLocationTables } from "@/reducer/LocationTablesState";
import type { useMaps } from "@/reducer/MapsState";
import type { useNotes } from "@/reducer/NoteState";
import type { usePlots } from "@/reducer/PlotsState";
import type { useTephigrams } from "@/reducer/TephigramState";
import type { useWeatherTables } from "@/reducer/WeatherTablesState";
import { getById, getToolByToolId } from "@/reducer/selectors/AccountSelectors";
import { type GenericLayout, LayoutType } from "@mm/metx-workbench.meteomatics.com";
import React, { type ReactNode } from "react";
import { HighlightType } from "./GridCellHighlightProvider";
import { GridCrossSection } from "./GridCrossSection";
import { GridEnergyPlot } from "./GridEnergyPlot";
import { GridNote } from "./GridNote/GridNote";
import { GridTephigram } from "./GridTephigram";
import { GridToolCell } from "./GridToolCell";

const GridWeatherTable = React.lazy(() =>
  import("./GridWeatherTable").then(({ GridWeatherTable }) => ({ default: GridWeatherTable })),
);

const GridMultiLocationTable = React.lazy(() =>
  import("./GridMultiLocationTable/GridMultiLocationTable").then(({ GridMultiLocationTable }) => ({
    default: GridMultiLocationTable,
  })),
);

/** All tools of the current tab, as read from the store by `Grid`. */
export interface GridTools {
  maps: ReturnType<typeof useMaps>;
  plots: ReturnType<typeof usePlots>;
  energyPlots: ReturnType<typeof useEnergyPlots>;
  weatherTables: ReturnType<typeof useWeatherTables>;
  locationTables: ReturnType<typeof useLocationTables>;
  tephigrams: ReturnType<typeof useTephigrams>;
  crossSections: ReturnType<typeof useCrossSections>;
  countryPlots: ReturnType<typeof useCountryPlots>;
  notes: ReturnType<typeof useNotes>;
}

interface ResolvedTool {
  /** Prefix of the React key, so switching the tool of a layout remounts the cell. */
  keyPrefix: string;
  featurePath: string;
  highlightType: HighlightType;
  toolId: number;
  variant: "map" | "plot";
  content: ReactNode;
}

function resolveTool(layout: GenericLayout, tools: GridTools): ResolvedTool | null | undefined {
  const cellLayout = layout.gridCellLayout;
  const toolId = layout.id_tool;
  if (!toolId) {
    return null;
  }

  switch (layout.type) {
    case LayoutType.Map: {
      const map = getById(toolId, tools.maps);
      return (
        map && {
          keyPrefix: "map",
          featurePath: "tools.map.enabled",
          highlightType: HighlightType.MAP,
          toolId: map.id,
          variant: "map",
          content: <GridMap map={map} cellLayout={cellLayout} />,
        }
      );
    }
    case LayoutType.Plot: {
      const plot = getToolByToolId(toolId, tools.plots);
      return (
        plot && {
          keyPrefix: "plot",
          featurePath: "tools.plot.enabled",
          highlightType: HighlightType.PLOT,
          toolId: plot.id,
          variant: "plot",
          content: <GridPlot plot={plot} cellLayout={cellLayout} />,
        }
      );
    }
    case LayoutType.EnergyPlot: {
      const energyPlot = getToolByToolId(toolId, tools.energyPlots);
      return (
        energyPlot && {
          keyPrefix: "energy-plot",
          featurePath: "tools.energy_plot.enabled",
          highlightType: HighlightType.ENERGY_PLOT,
          toolId: energyPlot.id,
          variant: "plot",
          content: <GridEnergyPlot energyPlot={energyPlot} cellLayout={cellLayout} />,
        }
      );
    }
    case LayoutType.WeatherTable: {
      const weatherTable = getToolByToolId(toolId, tools.weatherTables);
      return (
        weatherTable && {
          keyPrefix: "weather-table",
          featurePath: "tools.table.enabled",
          highlightType: HighlightType.WEATHER_TABLE,
          toolId: weatherTable.id,
          variant: "plot",
          content: (
            <React.Suspense fallback={null}>
              <GridWeatherTable weatherTable={weatherTable} cellLayout={cellLayout} />
            </React.Suspense>
          ),
        }
      );
    }
    case LayoutType.LocationTable: {
      const locationTable = getToolByToolId(toolId, tools.locationTables);
      return (
        locationTable && {
          keyPrefix: "location-table",
          featurePath: "tools.table.enabled",
          highlightType: HighlightType.LOCATION_TABLE,
          toolId: locationTable.id,
          variant: "plot",
          content: (
            <React.Suspense fallback={null}>
              <GridMultiLocationTable locationTable={locationTable} cellLayout={cellLayout} />
            </React.Suspense>
          ),
        }
      );
    }
    case LayoutType.Tephigram: {
      const tephigram = getToolByToolId(toolId, tools.tephigrams);
      return (
        tephigram && {
          keyPrefix: "tephigram",
          featurePath: "tools.skewt.enabled",
          highlightType: HighlightType.TEPHIGRAM,
          toolId: tephigram.id,
          variant: "plot",
          content: <GridTephigram tephigram={tephigram} cellLayout={cellLayout} />,
        }
      );
    }
    case LayoutType.CrossSection: {
      const crossSection = getToolByToolId(toolId, tools.crossSections);
      return (
        crossSection && {
          keyPrefix: "cross-section",
          featurePath: "tools.cross_section.enabled",
          highlightType: HighlightType.CROSS_SECTION,
          toolId: crossSection.id,
          variant: "plot",
          content: <GridCrossSection crossSection={crossSection} cellLayout={cellLayout} />,
        }
      );
    }
    case LayoutType.CountryPlot: {
      const countryPlot = getToolByToolId(toolId, tools.countryPlots);
      return (
        countryPlot && {
          keyPrefix: "country-plot",
          featurePath: "tools.country_forecast.enabled",
          highlightType: HighlightType.COUNTRY_PLOT,
          toolId: countryPlot.id,
          variant: "plot",
          content: <GridCountryPlot countryPlot={countryPlot} cellLayout={cellLayout} />,
        }
      );
    }
    case LayoutType.Notes: {
      const note = getToolByToolId(toolId, tools.notes);
      return (
        note && {
          keyPrefix: "note",
          featurePath: "tools.notes.enabled",
          highlightType: HighlightType.NOTE,
          toolId: note.id,
          variant: "plot",
          content: <GridNote note={note} />,
        }
      );
    }
    default:
      return null;
  }
}

interface GridLayoutCellProps {
  layout: GenericLayout;
  tabId: number;
  tools: GridTools;
  disabled: boolean;
  onDrop: (sourceId: number, targetId: number) => void;
}

/**
 * Renders a single layout of the grid: the tool it points to (behind its feature gate) or, if there is
 * no tool (or the feature is disabled), the empty cell with the tool picker.
 */
export function GridLayoutCell({ layout, tabId, tools, disabled, onDrop }: GridLayoutCellProps) {
  const emptyCell = (
    <GridSortableElement
      id={layout.id}
      disabled={disabled}
      gridCellLayout={layout.gridCellLayout}
      className="grid__cell--empty"
      key={`empty-${layout.id}`}
      onDrop={onDrop}
    >
      <GridEmptyOptions layout={layout} tabId={tabId} />
    </GridSortableElement>
  );

  const tool = resolveTool(layout, tools);
  if (!tool) {
    return emptyCell;
  }

  return (
    <GridToolCell
      key={`${tool.keyPrefix}-${tool.toolId}`}
      layout={layout}
      disabled={disabled}
      onDrop={onDrop}
      featurePath={tool.featurePath}
      highlightType={tool.highlightType}
      toolId={tool.toolId}
      variant={tool.variant}
      fallback={emptyCell}
    >
      {tool.content}
    </GridToolCell>
  );
}
